import React, { useState } from 'react'
import { Plus, X } from 'lucide-react'
import { Input } from '@/components/ui/Input'
import { Select } from '@/components/ui/Select'
import { Button } from '@/components/ui/Button'
import { TemplateFormField } from '@/services/templateApi'

interface DynamicFieldRendererProps {
  field: TemplateFormField
  value: string | number
  onChange: (value: string | number) => void
  error?: string
  className?: string
}

export const DynamicFieldRenderer: React.FC<DynamicFieldRendererProps> = ({
  field,
  value,
  onChange,
  error,
  className
}) => {
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const newValue = field.type === 'number' ? parseFloat(e.target.value) || 0 : e.target.value
    onChange(newValue)
  }

  const [showAddOption, setShowAddOption] = useState(false)
  const [newOption, setNewOption] = useState('')
  const [customOptions, setCustomOptions] = useState<string[]>([])

  const allOptions = [...(field.options || []), ...customOptions]

  const handleAddCustomOption = () => {
    if (newOption.trim() && !allOptions.includes(newOption.trim())) {
      setCustomOptions(prev => [...prev, newOption.trim()])
      onChange(newOption.trim())
      setNewOption('')
      setShowAddOption(false)
    }
  }

  const renderField = () => {
    switch (field.type) {
      case 'select':
        return (
          <div>
            <div className="flex gap-2 mb-2">
              <Select
                value={value as string}
                onValueChange={onChange as (value: string) => void}
                className={`flex-1 ${error ? 'border-red-500' : className}`}
                required={field.required}
              >
                <option value="">{field.placeholder || `Select ${field.label.toLowerCase()}...`}</option>
                {allOptions.map(option => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </Select>
              {(field.name === 'color' || field.name === 'style' || field.name === 'material_type' ||
                field.name === 'finish' || field.name === 'grade' || field.name === 'diameter' || field.name === 'manufacturer') && (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setShowAddOption(!showAddOption)}
                  leftIcon={<Plus className="w-3 h-3" />}
                >
                  Add
                </Button>
              )}
            </div>
            {showAddOption && (
              <div className="flex gap-2 p-2 bg-gray-50 dark:bg-gray-800 rounded border">
                <Input
                  type="text"
                  value={newOption}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => setNewOption(e.target.value)}
                  placeholder={`Add new ${field.label.toLowerCase()}...`}
                  className="flex-1"
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault()
                      handleAddCustomOption()
                    }
                  }}
                />
                <Button
                  type="button"
                  size="sm"
                  onClick={handleAddCustomOption}
                  disabled={!newOption.trim()}
                >
                  Add
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    setShowAddOption(false)
                    setNewOption('')
                  }}
                >
                  <X className="w-3 h-3" />
                </Button>
              </div>
            )}
          </div>
        )

      case 'multiselect':
        // For now, implement as a select with comma-separated values
        // Could be enhanced with a proper multi-select component later
        return (
          <Select
            value={value as string}
            onValueChange={onChange as (value: string) => void}
            className={error ? 'border-red-500' : className}
            required={field.required}
          >
            <option value="">{field.placeholder || `Select ${field.label.toLowerCase()}...`}</option>
            {field.options?.map(option => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </Select>
        )

      case 'number':
        return (
          <Input
            type="number"
            value={value}
            onChange={handleChange}
            placeholder={field.placeholder}
            className={error ? 'border-red-500' : className}
            required={field.required}
            min="0"
            step="0.01"
          />
        )

      case 'text':
      default:
        return (
          <Input
            type="text"
            value={value as string}
            onChange={handleChange}
            placeholder={field.placeholder}
            className={error ? 'border-red-500' : className}
            required={field.required}
          />
        )
    }
  }

  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
        {field.label} {field.required && '*'}
      </label>
      {renderField()}
      {error && (
        <p className="text-red-500 text-sm mt-1">{error}</p>
      )}
      {field.description && (
        <p className="text-xs text-gray-500 mt-1">{field.description}</p>
      )}
    </div>
  )
}